import request from "@/lib/api/handler";
import { toast } from "sonner";

import { useAuth0 } from "@auth0/auth0-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { QueryKeys } from "@/queries/utils";
import { TeamMember } from "@/queries/company/team";
import { AdminCompanyDetails } from "@/queries/admin/company-details";
import { CompanyUserAccountType } from "@/redux/features/companyProfileSlice";

export const useUpdateTeamMember = (companyId: string) => {
  const qc = useQueryClient();
  const { getAccessTokenSilently } = useAuth0();

  const { mutate, isPending, error } = useMutation<
    TeamMember,
    Error,
    { userId: string; accountType: CompanyUserAccountType }
  >({
    mutationFn: async (data) => {
      const token = await getAccessTokenSilently();

      return request<TeamMember>({
        method: "PATCH",
        path: `/api/company/${companyId}/team/${data.userId}`,
        body: JSON.stringify({
          accountType: data.accountType,
        }),
        token,
      });
    },
    onSettled: (_, error) => {
      if (error) {
        toast.error("Error updating team member, please try again");
      } else {
        toast.success("Team member updated!");
      }
    },
    onSuccess: (data) => {
      // Check for an existing companyTeam entry
      const prevCompanyTeam = qc.getQueryData<TeamMember[]>(
        QueryKeys.companyTeam(companyId),
      );

      if (prevCompanyTeam) {
        // If there is an existing entry, look for the updated element
        const copy = [...prevCompanyTeam];
        const entryIndex = copy.findIndex((user) => user.id === data.id);

        // If it was found, update it
        if (entryIndex !== -1 && copy[entryIndex]) {
          copy[entryIndex] = data;

          qc.setQueryData<TeamMember[]>(QueryKeys.companyTeam(companyId), copy);
          // Otherwise, invalidate and fetch new data
        } else {
          void qc.invalidateQueries({
            queryKey: QueryKeys.companyTeam(companyId),
          });
        }
      }

      // Same as above, but for company details
      const prevCompanyDetails = qc.getQueryData<AdminCompanyDetails>(
        QueryKeys.adminCompany(companyId),
      );

      if (prevCompanyDetails) {
        const copy = [...prevCompanyDetails.team];
        const entryIndex = copy.findIndex((user) => user.id === data.id);

        // If it was found, update it
        if (entryIndex !== -1 && copy[entryIndex]) {
          copy[entryIndex] = data;

          qc.setQueryData<AdminCompanyDetails>(
            QueryKeys.adminCompany(companyId),
            {
              ...prevCompanyDetails,
              team: copy,
            },
          );
          // Otherwise, invalidate and fetch new data
        } else {
          void qc.invalidateQueries({
            queryKey: QueryKeys.adminCompany(companyId),
          });
        }
      }
    },
  });

  return {
    updateTeamMember: mutate,
    isUpdatingTeamMember: isPending,
    teamMemberError: error,
  };
};
